import type { User } from "firebase/auth";
import { loadCharacterProfile } from "@/character/storage";
import { callPublishDungeon } from "@/cloud/game-callables";
import { HOME_SCENE_ID, loadSceneRoom } from "@/home/scene-room-storage";
import type { HomePartDefinition, HomeRoomData } from "@/home/storage";
import type { TowerFloorScenePlacement, TowerFloorSpawnRow } from "@/tower/tower-floor-config";
import { getDungeonFloorConfig, hydrateDungeonFloorConfigs } from "@/dungeon/dungeon-floor-config";
import type { DungeonMeta } from "@/dungeon/dungeon-meta";
import { dungeonSceneId } from "@/dungeon/dungeon-meta";
import { fitDungeonPublishPayloadForCloud } from "@/dungeon/dungeon-publish-fit";
import {
  validateDungeonFloorsForPublish,
  validateDungeonPublishPayloadSize,
} from "@/dungeon/dungeon-publish-validate";
import { loadHomeRoom, normalizeHomeRoom } from "@/home/storage";
import {
  buildTowerArenaAmbienceConfig,
  collectAmbiencePartIds,
  type TowerArenaAmbienceConfig,
} from "@/tower/tower-arena-ambience";
import {
  mergeHomeCatalogParts,
  resolveDungeonFloorScenePlacements,
} from "@/tower/tower-scene-room-adapter";

export type DungeonFloorPublishSnapshot = {
  floor: number;
  spawns: TowerFloorSpawnRow[];
  enabled: boolean;
  powerMultiplier?: number;
  scenePlacements?: TowerFloorScenePlacement[];
  sceneAmbience?: TowerArenaAmbienceConfig;
  sceneParts: HomePartDefinition[];
  monsterParts: HomePartDefinition[];
};

function collectPartIdsFromFloor(
  room: HomeRoomData,
  config: ReturnType<typeof getDungeonFloorConfig>,
): { sceneIds: Set<string>; monsterIds: Set<string> } {
  const sceneIds = new Set<string>();
  const monsterIds = new Set<string>();
  for (const p of config?.scenePlacements ?? []) {
    sceneIds.add(p.partId);
  }
  for (const row of config?.spawns ?? []) {
    monsterIds.add(row.monsterId);
  }
  for (const placement of room.placements) {
    sceneIds.add(placement.partId);
  }
  for (const partId of collectAmbiencePartIds(room)) {
    sceneIds.add(partId);
  }
  return { sceneIds, monsterIds };
}

function partsFromRoom(room: HomeRoomData, ids: Set<string>): HomePartDefinition[] {
  return room.parts.filter((part) => ids.has(part.id));
}

/** Defender home visit: only placed parts, not the full catalog. */
function homeRoomForRaidPublish(homeScene: HomeRoomData, catalog: HomeRoomData): HomeRoomData {
  const merged = mergeHomeCatalogParts(homeScene, catalog);
  const placedIds = new Set(homeScene.placements.map((p) => p.partId));
  return normalizeHomeRoom({
    ...homeScene,
    parts: merged.parts.filter((part) => placedIds.has(part.id)),
  });
}

export async function buildDungeonPublishPayload(
  user: User,
  meta: DungeonMeta,
): Promise<{
  published: boolean;
  maxFloors: number;
  floors: DungeonFloorPublishSnapshot[];
  homeRoom: HomeRoomData;
  displayName: string;
}> {
  const profile = loadCharacterProfile(user.uid);
  const displayName = profile?.name ?? "ผู้เล่น";
  await hydrateDungeonFloorConfigs(user.uid);

  const floors: DungeonFloorPublishSnapshot[] = [];
  for (let f = 1; f <= meta.maxFloors; f += 1) {
    const config = getDungeonFloorConfig(f);
    const room = await loadSceneRoom(user.uid, dungeonSceneId(f));
    const homeCatalog = await loadHomeRoom(user.uid);
    const roomWithParts = mergeHomeCatalogParts(room, homeCatalog);
    const { sceneIds, monsterIds } = collectPartIdsFromFloor(roomWithParts, config);
    const scenePlacements = resolveDungeonFloorScenePlacements(
      roomWithParts,
      config?.scenePlacements,
    );
    for (const p of scenePlacements) {
      sceneIds.add(p.partId);
    }
    floors.push({
      floor: f,
      spawns: config?.spawns ?? [],
      enabled: config?.enabled ?? false,
      powerMultiplier: config?.powerMultiplier,
      scenePlacements,
      sceneAmbience: buildTowerArenaAmbienceConfig(roomWithParts),
      sceneParts: partsFromRoom(roomWithParts, sceneIds),
      monsterParts: partsFromRoom(roomWithParts, monsterIds),
    });
  }

  const homeRoom = await loadSceneRoom(user.uid, HOME_SCENE_ID);
  const homeBase = await loadHomeRoom(user.uid);
  const homeMerged = homeRoomForRaidPublish(homeRoom, homeBase);

  return {
    published: meta.published,
    maxFloors: meta.maxFloors,
    floors,
    homeRoom: homeMerged,
    displayName,
  };
}

export async function publishDungeonToCloud(
  user: User,
  meta: DungeonMeta,
): Promise<{ imageCompressed: boolean }> {
  const payload = await buildDungeonPublishPayload(user, meta);
  const floorCheck = validateDungeonFloorsForPublish(meta, payload.floors);
  if (!floorCheck.ok) {
    throw new Error(floorCheck.reason);
  }

  const { body, imageCompressed } = await fitDungeonPublishPayloadForCloud({
    floors: payload.floors,
    homeRoom: payload.homeRoom,
  });

  const sizeCheck = validateDungeonPublishPayloadSize({
    floors: body.floors,
    homeRoom: body.homeRoom,
  });
  if (!sizeCheck.ok) {
    throw new Error(sizeCheck.reason);
  }
  await callPublishDungeon({
    published: meta.published,
    maxFloors: payload.maxFloors,
    floors: body.floors,
    homeRoom: body.homeRoom,
    displayName: payload.displayName,
  });
  return { imageCompressed };
}
